import { motion } from 'framer-motion';
import { useMemo } from 'react';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Card } from '../../components';
import type { AppData } from '../../data/types';
import { txContributionInRange } from '../../lib/budget';
import { formatCurrency } from '../../lib/currency';
import { getPayPeriod } from '../../lib/payPeriod';
import { useResolvedTheme } from '../../lib/useResolvedTheme';
import { springGentle } from '../../tokens';
import { CHART_PALETTE } from './chartTheme';

type Props = {
  data: AppData;
  today: Date;
  onCategoryTap?: (categoryId: string) => void;
};

/**
 * Donut chart of this period's spending grouped by category. Center
 * label shows the period total; legend below lists each category with
 * its dollar amount + percentage.
 */
export function SpendingByCategory({ data, today, onCategoryTap }: Props) {
  const theme = useResolvedTheme();
  const { slices, total } = useMemo(() => {
    const period = getPayPeriod(data.profile, today);
    const sums = new Map<string, number>();
    for (const t of data.transactions) {
      if (t.type !== 'expense') continue;
      const amt = txContributionInRange(t, period.start, period.end);
      if (amt > 0) {
        sums.set(t.categoryId, (sums.get(t.categoryId) ?? 0) + amt);
      }
    }
    const total = [...sums.values()].reduce((a, b) => a + b, 0);
    const slices = [...sums.entries()]
      .map(([id, value]) => {
        const cat = data.categories.find((c) => c.id === id);
        return {
          id,
          name: cat?.name ?? 'Uncategorized',
          value,
          pct: total > 0 ? value / total : 0,
        };
      })
      .sort((a, b) => b.value - a.value)
      .map((s, i) => ({
        ...s,
        color: CHART_PALETTE[i % CHART_PALETTE.length]!,
      }));
    return { slices, total };
  }, [data, today]);

  const currency = data.profile.currency;
  const sliceStroke = theme === 'dark' ? '#18181B' : '#FFFFFF';

  return (
    <Card elevation="sm" className="p-5 rounded-[24px]">
      <h2 className="text-[17px] font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
        Spending by category
      </h2>
      {slices.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-text-secondary">
          No expenses logged this period yet.
        </p>
      ) : (
        <>
          <div className="relative h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive
                  animationDuration={650}
                  stroke={sliceStroke}
                  strokeWidth={2}
                  onClick={(p) => {
                    const id = (p as { id?: string }).id;
                    if (id) onCategoryTap?.(id);
                  }}
                >
                  {slices.map((s) => (
                    <Cell key={s.id} fill={s.color} cursor="pointer" />
                  ))}
                </Pie>
                <Tooltip
                  separator=": "
                  formatter={(v) =>
                    formatCurrency(Number(v) || 0, { currency })
                  }
                  contentStyle={{
                    background: 'rgb(var(--surface))',
                    border: '1px solid rgb(var(--surface-secondary))',
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-[11px] uppercase tracking-[0.05em] text-text-tertiary">
                Total
              </div>
              <div className="text-[20px] font-mono tabular-nums text-text-primary">
                {formatCurrency(total, { currency })}
              </div>
            </div>
          </div>
          <motion.div
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.04 } },
            }}
            className="mt-4 flex flex-col gap-2"
          >
            {slices.slice(0, 6).map((s) => (
              <motion.button
                key={s.id}
                type="button"
                variants={{ hidden: { opacity: 0, y: 4 }, show: { opacity: 1, y: 0 } }}
                transition={springGentle}
                onClick={() => onCategoryTap?.(s.id)}
                className="flex items-center justify-between gap-3 text-left"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-pill shrink-0"
                    style={{ backgroundColor: s.color }}
                  />
                  <span className="text-[13px] text-text-primary truncate">
                    {s.name}
                  </span>
                </div>
                <span className="text-[12px] font-mono tabular-nums text-text-secondary shrink-0">
                  {formatCurrency(s.value, { currency })}{' '}
                  <span className="text-text-tertiary">
                    {(s.pct * 100).toFixed(0)}%
                  </span>
                </span>
              </motion.button>
            ))}
          </motion.div>
        </>
      )}
    </Card>
  );
}
